import { Link } from 'react-router-dom';
import { Heart, Shield } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEOHead from '../components/SEOHead';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEOHead title="Privacy Policy" description="LusoLove privacy policy. Learn how we protect your data and respect your privacy on the Portuguese dating platform." path="/privacy" />
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#FAF9F6]/90 border-b border-[#E5E3DB]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo">
            <Heart className="w-7 h-7 text-[#6E1A24] group-hover:scale-110 transition-transform" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            <span className="text-xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <div className="pt-28 pb-20 px-6 sm:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#6E1A24]/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#6E1A24]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-serif font-light text-[#2D2D2A]" data-testid="privacy-title">
                Privacy Policy
              </h1>
              <p className="text-sm text-[#6C6C67] mt-1">Last updated: 09.04.2026</p>
            </div>
          </div>

          <p className="text-[#6C6C67] leading-relaxed mb-10">
            LusoLove is a dating platform connecting Portuguese singles across Europe and Portugal.
          </p>

          <div className="space-y-10">
            <Section number="1" title="Data We Collect">
              <ul className="list-disc list-inside space-y-2 text-[#6C6C67] leading-relaxed">
                <li>Name and profile information</li>
                <li>Email address</li>
                <li>Photos uploaded by users</li>
                <li>Preferences and profile details</li>
                <li>Messages exchanged on the platform</li>
                <li>Technical data (IP address, device, browser)</li>
              </ul>
            </Section>

            <Section number="2" title="How We Use Data">
              <ul className="list-disc list-inside space-y-2 text-[#6C6C67] leading-relaxed">
                <li>Provide and improve the service</li>
                <li>Match users</li>
                <li>Enable communication</li>
                <li>Ensure safety and prevent abuse</li>
                <li>Process subscriptions and payments</li>
              </ul>
            </Section>

            <Section number="3" title="Data Sharing">
              <p className="text-[#6C6C67] leading-relaxed mb-3">We do not sell personal data. We may share data with:</p>
              <ul className="list-disc list-inside space-y-2 text-[#6C6C67] leading-relaxed">
                <li>Braintree/PayPal (payments)</li>
                <li>Hosting providers</li>
                <li>Legal authorities if required</li>
              </ul>
            </Section>

            <Section number="4" title="Data Storage">
              <p className="text-[#6C6C67] leading-relaxed">
                Data is stored securely and only as long as necessary.
              </p>
            </Section>

            <Section number="5" title="User Rights">
              <p className="text-[#6C6C67] leading-relaxed mb-3">Users can:</p>
              <ul className="list-disc list-inside space-y-2 text-[#6C6C67] leading-relaxed">
                <li>Access their data</li>
                <li>Edit their profile</li>
                <li>Delete their account</li>
              </ul>
            </Section>

            <Section number="6" title="Security">
              <p className="text-[#6C6C67] leading-relaxed">
                We implement reasonable security measures.
              </p>
            </Section>

            <Section number="7" title="Cookies">
              <p className="text-[#6C6C67] leading-relaxed">
                Used to improve experience and analytics.
              </p>
            </Section>

            <Section number="8" title="Contact">
              <p className="text-[#6C6C67] leading-relaxed">
                <a href="mailto:contact@lusolove.com" className="text-[#6E1A24] hover:underline">
                  contact@lusolove.com
                </a>
              </p>
            </Section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 bg-[#2D2D2A] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-[#C97A63]" strokeWidth={1.5} fill="currentColor" fillOpacity={0.3} />
            <span className="text-xl font-serif">LusoLove</span>
          </div>
          <div className="flex gap-8 text-sm text-white/60">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link>
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
          <p className="text-sm text-white/40">Made in Portugal, with love.</p>
        </div>
      </footer>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div data-testid={`privacy-section-${number}`}>
      <h2 className="text-lg font-serif font-medium text-[#2D2D2A] mb-3">
        <span className="text-[#6E1A24] mr-2">{number}.</span>
        {title}
      </h2>
      {children}
    </div>
  );
}
